/**
 * @file     StreakBadge.tsx
 * @readme   ../README.md
 * @purpose  Displays current streak count with custom SVG flame icon, pulse animation on active, and grace period warning.
 */
'use client';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme';
import { useAcademy } from '../AcademyContext';
import { isStreakActive, isStreakInGrace } from '../streak';
import { IconStreak } from '../icons/AcademyIcons';

export function StreakBadge() {
  const { theme } = useAppTheme();
  const { streak } = useAcademy();
  const dur = theme.motion.durations;
  const ease = theme.motion.easing;

  if (!streak || streak.currentDays === 0) return null;

  const active = isStreakActive(streak);
  const inGrace = isStreakInGrace(streak);
  const tooltipText = `${streak.currentDays}-day learning streak${inGrace ? ' (expires soon -- complete a lesson today!)' : ''}`;

  return (
    <Tooltip title={tooltipText} arrow>
      <Box
        aria-label={tooltipText}
        role="status"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: `${theme.layout.radius.pill}px`,
          backgroundColor: active
            ? alpha(theme.rawSemanticColors.statusWarning, 0.1)
            : alpha(theme.rawSemanticColors.textMuted, 0.06),
          border: `1px solid ${active ? alpha(theme.rawSemanticColors.statusWarning, 0.25) : theme.semanticColors.borderSubtle}`,
          transition: `all ${dur.normal}ms ${ease.standard}`,
          '@keyframes streak-pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' },
          },
          '@media (prefers-reduced-motion: no-preference)': {
            ...(active ? { animation: `streak-pulse 2s ${ease.standard} infinite` } : {}),
          },
        }}
      >
        <IconStreak size={16} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.semanticColors.textPrimary }}>
          {streak.currentDays}
        </Typography>
        {inGrace && (
          <Typography sx={{ fontSize: 10, color: theme.semanticColors.statusWarning, fontWeight: 500, letterSpacing: 0.5 }}>
            EXPIRING
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
